import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDatePicker,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { CalendarIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { getTodayYYYYMMDD } from "@/shared/lib/date-time";
import {
  formatLocalDateOnly,
  parseLocalDateOnly,
} from "@/shared/lib/datetime-local-form";
import type { DocumentDTO } from "../../domain/contracts";
import { useIssueDocument } from "../api/queries";

const DOCUMENT_TYPE_LABELS = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
} as const;

const IssueDocumentFormSchema = z.object({
  issuedAt: z.date({ required_error: "ຕ້ອງເລືອກວັນທີອອກເອກະສານ" }),
});

type IssueDocumentFormValues = z.infer<typeof IssueDocumentFormSchema>;

function defaultIssueDate(): Date {
  return parseLocalDateOnly(getTodayYYYYMMDD()) ?? new Date();
}

type IssueDocumentDialogProps = {
  eventId: string;
  document: Pick<DocumentDTO, "id" | "documentType"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBeforeIssue?: () => void | Promise<void>;
};

export function IssueDocumentDialog({
  eventId,
  document,
  open,
  onOpenChange,
  onBeforeIssue,
}: IssueDocumentDialogProps) {
  const issueDocument = useIssueDocument(eventId);
  const methods = RHF.useForm<IssueDocumentFormValues>({
    resolver: zodResolver(IssueDocumentFormSchema),
    defaultValues: { issuedAt: defaultIssueDate() },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({ issuedAt: defaultIssueDate() });
  }, [open, document?.id, methods]);

  const documentLabel = document
    ? DOCUMENT_TYPE_LABELS[document.documentType]
    : "ເອກະສານ";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="size-5" />
            ອອກ{documentLabel}
          </DialogTitle>
          <DialogDescription>
            ເລືອກວັນທີທີ່ສະແດງບົນເອກະສານ — ຫຼັງອອກແລ້ວ ລາຍການຈະຖືກລັອກ
          </DialogDescription>
        </DialogHeader>

        <FormRoot
          methods={methods}
          onSubmit={async (values) => {
            if (!document) return;
            try {
              await onBeforeIssue?.();
              const issuedAt =
                parseLocalDateOnly(formatLocalDateOnly(values.issuedAt)) ??
                values.issuedAt;
              await issueDocument.mutateAsync({
                documentId: document.id,
                issuedAt: formatLocalDateOnly(issuedAt),
              });
              onOpenChange(false);
            } catch {
              // toast handled by mutation
            }
          }}
          className="space-y-4"
        >
          <FormDatePicker
            name="issuedAt"
            label="ວັນທີອອກເອກະສານ"
            requiredMark
            placeholder="ເລືອກວັນທີ..."
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={issueDocument.isPending}>
              {issueDocument.isPending ? "ກຳລັງອອກ..." : "ອອກເອກະສານ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
